
import { supabase } from '@/integrations/supabase/client';
import { secureStorage } from '@/lib/secure-storage';

export type CardExpenseDisplayDate = 'due_date' | 'transaction_date';

export interface BusinessProfile {
  id: string;
  user_id: string;
  name: string;
  type: 'personal' | 'business';
  color: string;
  is_active: boolean;
  timezone: string;
  created_at: string;
  updated_at: string;
  card_expense_display_date?: CardExpenseDisplayDate;
}

export const loadProfilesFromSupabase = async (): Promise<BusinessProfile[]> => {
  console.log('[Supabase Profiles] Loading profiles from Supabase...');

  const { data, error } = await supabase
    .from('business_profiles')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[Supabase Profiles] Error loading profiles:', error);
    throw new Error(`Erro ao carregar perfis: ${error.message}`);
  }

  console.log(`[Supabase Profiles] Loaded ${data?.length || 0} profiles`);
  return (data || []).map(row => ({
    ...row,
    type: row.type as 'personal' | 'business',
    card_expense_display_date: (row.card_expense_display_date as CardExpenseDisplayDate) || 'due_date'
  }));
};

export const saveProfileToSupabase = async (profile: Omit<BusinessProfile, 'id' | 'created_at' | 'updated_at' | 'user_id'>): Promise<BusinessProfile> => {
  console.log('[Supabase Profiles] Saving profile:', profile.name);
  
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) {
    throw new Error('Usuário não autenticado');
  }

  const profileData = {
    ...profile,
    user_id: user.user.id
  };

  const { data, error } = await supabase
    .from('business_profiles')
    .insert([profileData])
    .select()
    .single();

  if (error) {
    console.error('[Supabase Profiles] Error saving profile:', error);
    // Check for unique constraint violation (duplicate name)
    if (error.code === '23505' && error.message?.includes('unique_user_profile_name')) {
      throw new Error('Já existe um perfil com esse nome. Escolha outro nome.');
    }
    throw new Error(`Erro ao salvar perfil: ${error.message}`);
  }

  console.log('[Supabase Profiles] Profile saved successfully:', data.id);
  return {
    ...data,
    type: data.type as 'personal' | 'business',
    card_expense_display_date: (data.card_expense_display_date as CardExpenseDisplayDate) || 'due_date'
  };
};

export const updateProfileInSupabase = async (id: string, updates: Partial<BusinessProfile>): Promise<void> => {
  console.log('[Supabase Profiles] Updating profile:', id);

  const { error } = await supabase
    .from('business_profiles')
    .update(updates)
    .eq('id', id);

  if (error) {
    console.error('[Supabase Profiles] Error updating profile:', error);
    // Check for unique constraint violation (duplicate name)
    if (error.code === '23505' && error.message?.includes('unique_user_profile_name')) {
      throw new Error('Já existe um perfil com esse nome. Escolha outro nome.');
    }
    throw new Error(`Erro ao atualizar perfil: ${error.message}`);
  }

  console.log('[Supabase Profiles] Profile updated successfully:', id);
};

export const deleteProfileFromSupabase = async (id: string): Promise<void> => {
  console.log('[Supabase Profiles] Deleting profile:', id);

  const { error } = await supabase
    .from('business_profiles')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('[Supabase Profiles] Error deleting profile:', error);
    throw new Error(`Erro ao excluir perfil: ${error.message}`);
  }

  console.log('[Supabase Profiles] Profile deleted successfully:', id);
};

export const isUserAuthenticated = async (): Promise<boolean> => {
  const { data: { user } } = await supabase.auth.getUser();
  return !!user;
};

export const getCurrentActiveProfileId = (): string | null => {
  const profileId = secureStorage.getItemSync<string>('activeProfileId');
  console.log('[Profile Debug] Getting current profile ID from secure storage:', profileId);
  return profileId;
};

export const setCurrentActiveProfileId = (profileId: string): void => {
  secureStorage.setItemSync('activeProfileId', profileId);
};
